import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/lunelle/Logo";
import { usePageMeta } from "@/lib/usePageMeta";
import { resetPassword } from "@/lib/account-api";
import { formatApiError } from "@/lib/admin-api";
import { useAccount } from "@/lib/account";

const inputCls = "mt-1.5 h-11 w-full rounded-xl border border-[#C86D51]/25 bg-[#FDFBF7] px-3 text-[#2C221E] outline-none focus:border-[#9E4429]";

export default function ResetPasswordPage() {
  usePageMeta("Reset your password — Lunelle", "Choose a new password for your Lunelle account.");
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const { openAuth } = useAccount();
  const [pw, setPw] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== again) { setError("The two passwords don't match."); return; }
    setBusy(true);
    setError("");
    try {
      await resetPassword(token, pw);
      toast("Password updated", { description: "Sign in with your new password." });
      navigate("/", { replace: true });
      openAuth("signin");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto min-h-screen max-w-md px-5 py-10" data-testid="reset-password-page">
      <div className="flex items-center justify-between">
        <Link to="/" data-testid="reset-home-link"><Logo /></Link>
        <Link to="/" className="flex items-center gap-1.5 text-sm text-[#5C4A42]"><ArrowLeft className="h-4 w-4" />Home</Link>
      </div>
      <h1 className="mt-16 font-serif text-4xl font-light text-[#2C221E] sm:text-5xl">Choose a <em className="italic text-[#9E4429]">new password.</em></h1>
      {!token ? (
        <p className="mt-6 text-[#5C4A42]" data-testid="reset-missing-token">This link is missing its reset code. Open the link from your email again, or request a new one from Sign in.</p>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-password-form">
          <label className="block text-sm text-[#5C4A42]">New password (8+ characters)
            <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="reset-password-input" className={inputCls} />
          </label>
          <label className="block text-sm text-[#5C4A42]">Type it again
            <input type="password" required minLength={8} value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" data-testid="reset-password-confirm-input" className={inputCls} />
          </label>
          {error && <p role="alert" className="text-sm text-[#A9503A]" data-testid="reset-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="reset-submit-button"
            className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#2C221E] text-sm font-semibold text-[#FBF5EE] transition-colors hover:bg-[#9E4429] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Save new password
          </button>
        </form>
      )}
    </main>
  );
}
